import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import * as echarts from 'echarts'
import { ResizeService } from '@/services/resize.service'
import { getAssignBarOption } from '@/utils/echarts-option/bar'
import { deepAssign, getLegendDefaultOption } from '@/utils/echarts-option/base'
import { LEGEND_RANK_OPTION } from '@/utils/echarts-option/rank'
import { useAxisLabelTooltip, useAxisLabelHover } from '@/utils/echarts-tool/base'
import { getCssColorVar } from '@/utils/chart-theme-colors'
import { useEchartsThemeSync } from '@/composables/use-echarts-theme-sync'
import type { ChannelDistributionData } from '../../types'


export default /*#__PURE__*/_defineComponent({
  __name: 'channel-distribution-chart',
  props: {
    data: { default: undefined }
  },
  setup(__props: any) {

const { t, locale } = useI18n()

const props = __props

const fallbackData = computed<ChannelDistributionData>(() => {
  void locale.value
  return {
    categories: [
      t('overviewZone.channelTypeNetwork'),
      t('overviewZone.channelTypeIm'),
      t('overviewZone.channelTypeNetdisk'),
      t('overviewZone.channelTypeDevice'),
      t('overviewZone.channelTypeOther')
    ],
    series: [
      { name: t('outboundControlLog.sendStatusReleased'), values: [42, 35, 38, 55, 0] },
      { name: t('outboundControlLog.sendStatusBlocked'), values: [28, 45, 30, 35, 0] }
    ]
  }
})

const resolvedData = computed(() => props.data ?? fallbackData.value)

const injector = useReflectiveInjector([ResizeService])
const resizeService = injector.get(ResizeService)

const chartRef = ref<HTMLElement>()
let chartInstance: echarts.EChartsType | null = null

function createChart () {
  if (!chartRef.value) return
  chartInstance = echarts.init(chartRef.value)

  nextTick(() => {
    if (chartInstance) {
      useAxisLabelTooltip(chartInstance, 'x')
      useAxisLabelHover(chartInstance, 'x')
    }
  })
}

function getSeries () {
  return resolvedData.value.series.map((item, seriesIndex) => ({
    name: item.name,
    type: 'bar' as const,
    data: item.values,
    barMaxWidth: 44,
    barGap: '8%',
    barCategoryGap: '22%',
    itemStyle: {
      color: seriesIndex === 0 ? getCssColorVar('--el-color-primary', '#0057B4') : '#808D9C', /* dm-keep：#808D9C 中灰蓝深浅均可见 */
      borderRadius: [6, 6, 0, 0],
    },
  }))
}

function getChartOption () {
  const LABEL_MAX_LENGTH = 6

  /** 图例仍在底部；收紧 grid.bottom、轴标签 margin，减少柱体与类目文字之间的空洞，同时保留较粗的柱宽 */
  const option = {
    grid: {
      top: 36,
      bottom: 46,
      left: 44,
      right: 16,
      containLabel: true,
    },
    legend: {
      ...deepAssign(deepAssign({}, getLegendDefaultOption()), LEGEND_RANK_OPTION),
      top:undefined,
      left: 'center',
      bottom: 0,
      itemGap: 20,
    },
    xAxis: {
      data: resolvedData.value.categories,
      triggerEvent: true,
      axisLabel: {
        interval: 0,
        rotate: 0,
        formatter: (value: string) => {
          return value.length > LABEL_MAX_LENGTH ? `${value.slice(0, LABEL_MAX_LENGTH)}...` : value
        },
      },
    },
    series: getSeries(),
  }

  return getAssignBarOption(option, { unit: t('overviewZone.channelChartUnitTimes') })
}

function renderChart () {
  if (!chartInstance) return
  chartInstance.setOption(getChartOption())
}

function initChart () {
  chartInstance && chartInstance.dispose()
  createChart()
  renderChart()
}

const resizeSubscribe = resizeService.resizeSubject.subscribe(() => {
  chartInstance && chartInstance.resize()
})

let resizeObserver: ResizeObserver | null = null

watch(
  () => resolvedData.value,
  () => {
    nextTick(() => renderChart())
  },
  { deep: true }
)

watch(locale, () => {
  nextTick(() => renderChart())
})

useEchartsThemeSync(renderChart)

onMounted(() => {
  setTimeout(() => initChart(), 100)
  nextTick(() => {
    if (!chartRef.value || typeof ResizeObserver === 'undefined') return
    resizeObserver = new ResizeObserver(() => {
      chartInstance?.resize()
    })
    resizeObserver.observe(chartRef.value)
  })
})

onUnmounted(() => {
  resizeObserver?.disconnect()
  resizeObserver = null
  resizeSubscribe.unsubscribe()
  chartInstance && chartInstance.dispose()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "chartRef",
    ref: chartRef,
    class: "channel-distribution-chart-wrap"
  }, null, 512))
}
}

})